<template>
  <div class="od-wrap">
    <div v-if="loading" class="od-loading">
      <div class="od-spinner"></div> Загружаем заказ...
    </div>

    <div v-else-if="!order" class="od-not-found">
      <div class="od-nf-icon">🔍</div>
      <h2>Заказ не найден</h2>
      <p>Возможно, он был удалён или у вас нет доступа</p>
      <router-link to="/orders" class="od-btn-back-link">← Вернуться к заказам</router-link>
    </div>

    <div v-else>
      <!-- Хлебные крошки -->
      <div class="od-breadcrumb">
        <router-link to="/orders" class="od-breadcrumb-link">Мои заказы</router-link>
        <span>›</span>
        <span>{{ order.orderNumber }}</span>
      </div>

      <!-- Шапка -->
      <div class="od-header">
        <div>
          <h1 class="od-title">{{ order.orderNumber }}</h1>
          <div class="od-created">Создан {{ formatDate(order.createdAt) }}</div>
        </div>
        <div class="od-status-badge" :class="getStatusClass(order.status)">
          {{ getStatusLabel(order.status) }}
        </div>
      </div>

      <!-- Real-time индикатор -->
      <div v-if="liveConnected" class="od-live-badge">
        <span class="od-live-dot"></span> Отслеживание в реальном времени
      </div>
      <div v-if="liveUpdate" class="od-live-toast">
        🔔 Статус обновлён: <strong>{{ getStatusLabel(liveUpdate.status) }}</strong>
      </div>

      <!-- Прогресс ремонта -->
      <div class="od-progress-card">
        <div class="od-pc-title">Прогресс ремонта</div>
        <div class="od-steps-row">
          <div v-for="(step, i) in progressSteps" :key="i" class="od-step" :class="getStepClass(order.status, i)">
            <div class="od-step-circle">
              <svg v-if="getStepClass(order.status, i) === 'od-step-done'" width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M2.5 7l3 3 6-6" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span v-else>{{ i + 1 }}</span>
            </div>
            <div class="od-step-label">{{ step.label }}</div>
            <div class="od-step-sub">{{ step.sub }}</div>
          </div>
        </div>
        <div class="od-progress-bar">
          <div class="od-progress-fill" :style="{ width: getProgressWidth(order.status) }"></div>
        </div>
      </div>

      <!-- Основная инфа в сетке -->
      <div class="od-grid">

        <!-- Устройство -->
        <div class="od-card">
          <div class="od-card-title">📱 Устройство</div>
          <div class="od-row"><span>Бренд</span><strong>{{ order.deviceInfo?.brand }}</strong></div>
          <div class="od-row"><span>Модель</span><strong>{{ order.deviceInfo?.model }}</strong></div>
          <div class="od-row"><span>Проблема</span><strong>{{ order.deviceInfo?.issue }}</strong></div>
          <div class="od-row" v-if="order.deviceInfo?.imei"><span>IMEI</span><strong>{{ order.deviceInfo.imei }}</strong></div>
        </div>

        <!-- Услуги -->
        <div class="od-card">
          <div class="od-card-title">🔧 Услуги</div>
          <div v-for="(item, idx) in order.services" :key="idx" class="od-service-row">
            <div>
              <div class="od-svc-name">{{ item.service?.name || 'Услуга' }}</div>
              <div class="od-svc-desc">{{ item.service?.description }}</div>
            </div>
            <div class="od-svc-price">₴{{ item.price }}</div>
          </div>
          <div class="od-total-row">
            <span>Итого</span>
            <strong class="od-total-val">₴{{ order.totalPrice }}</strong>
          </div>
        </div>

        <!-- График -->
        <div class="od-card" v-if="order.startDate || order.estimatedCompletionDate || order.completionDate">
          <div class="od-card-title">🕐 График работы</div>
          <div class="od-row" v-if="order.startDate"><span>Начало работы</span><strong>{{ formatDate(order.startDate) }}</strong></div>
          <div class="od-row" v-if="order.estimatedCompletionDate"><span>Ожидаемая готовность</span><strong>{{ formatDate(order.estimatedCompletionDate) }}</strong></div>
          <div class="od-row od-row-green" v-if="order.completionDate"><span>Завершено</span><strong>{{ formatDate(order.completionDate) }}</strong></div>
        </div>

        <!-- Оплата -->
        <div class="od-card">
          <div class="od-card-title">💳 Оплата</div>
          <div class="od-row">
            <span>Статус оплаты</span>
            <div class="od-pay-badge" :class="getPaymentClass(order.paymentStatus)">{{ getPaymentLabel(order.paymentStatus) }}</div>
          </div>
          <div class="od-row" v-if="order.paymentMethod"><span>Способ</span><strong>{{ order.paymentMethod }}</strong></div>
          <div class="od-total-row">
            <span>Сумма</span>
            <strong class="od-total-val">₴{{ order.totalPrice }}</strong>
          </div>
        </div>

        <!-- Техник -->
        <div class="od-card" v-if="order.assignedTechnician">
          <div class="od-card-title">👨‍🔧 Мастер</div>
          <div class="od-technician">
            <div class="od-tech-avatar">{{ order.assignedTechnician.name?.charAt(0) || 'М' }}</div>
            <div>
              <div class="od-tech-name">{{ order.assignedTechnician.name }}</div>
              <div class="od-tech-phone">{{ order.assignedTechnician.phone }}</div>
            </div>
          </div>
        </div>

        <!-- Примечания -->
        <div class="od-card od-notes" v-if="order.notes">
          <div class="od-card-title">📝 Примечания мастера</div>
          <div class="od-notes-text">{{ order.notes }}</div>
        </div>

      </div>

      <!-- Действия -->
      <div class="od-actions">
        <router-link to="/orders" class="od-btn-secondary">← Все заказы</router-link>
        <button
          v-if="!['completed', 'cancelled'].includes(order.status)"
          @click="cancelOrder"
          class="od-btn-cancel"
        >
          Отменить заказ
        </button>
      </div>

    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '../stores/auth';
import { useSocket } from '../composables/useSocket';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

export default {
  name: 'OrderDetailPage',
  setup() {
    const route = useRoute();
    const router = useRouter();
    const authStore = useAuthStore();
    const order = ref(null);
    const loading = ref(false);
    const liveUpdate = ref(null);
    const liveToastTimer = ref(null);

    const { connected: liveConnected, joinOrderRoom, leaveOrderRoom, onOrderStatusUpdate, offOrderStatusUpdate } = useSocket();

    const progressSteps = [
      { label: 'Принят',      sub: 'Заявка получена' },
      { label: 'Диагностика', sub: 'Проверяем устройство' },
      { label: 'Ремонт',      sub: 'Выполняем работу' },
      { label: 'Проверка',    sub: 'Тестируем результат' },
      { label: 'Готов',       sub: 'Можно забирать' },
    ];

    const statusOrder = { pending: 0, accepted: 1, 'in-progress': 2, completed: 4, cancelled: -1 };
    const statusLabels = { pending: 'Ожидание', accepted: 'Принят', 'in-progress': 'В работе', completed: 'Завершён', cancelled: 'Отменён' };
    const paymentLabels = { unpaid: 'Не оплачен', paid: 'Оплачен', partial: 'Частично' };

    function getStatusLabel(s) { return statusLabels[s] || s; }
    function getStatusClass(s) {
      return { pending: 'badge-yellow', accepted: 'badge-blue', 'in-progress': 'badge-purple', completed: 'badge-green', cancelled: 'badge-red' }[s] || '';
    }
    function getPaymentLabel(s) { return paymentLabels[s] || s; }
    function getPaymentClass(s) {
      return { unpaid: 'pay-red', paid: 'pay-green', partial: 'pay-yellow' }[s] || '';
    }
    function getStepClass(orderStatus, i) {
      const cur = statusOrder[orderStatus] ?? -1;
      if (cur < 0) return '';
      if (i < cur) return 'od-step-done';
      if (i === cur) return 'od-step-active';
      return '';
    }
    function getProgressWidth(status) {
      return { pending: '8%', accepted: '28%', 'in-progress': '58%', completed: '100%', cancelled: '0%' }[status] || '0%';
    }
    function formatDate(date) {
      if (!date) return 'Не указано';
      return new Date(date).toLocaleDateString('ru-UA', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    }

    function handleStatusUpdate(data) {
      if (order.value) {
        order.value.status = data.status;
        if (data.paymentStatus) order.value.paymentStatus = data.paymentStatus;
      }
      liveUpdate.value = data;
      if (liveToastTimer.value) clearTimeout(liveToastTimer.value);
      liveToastTimer.value = setTimeout(() => { liveUpdate.value = null; }, 5000);
    }

    async function loadOrder() {
      loading.value = true;
      try {
        const res = await axios.get(`${API_URL}/orders/${route.params.id}`, { headers: { Authorization: `Bearer ${authStore.token}` } });
        order.value = res.data;
      } catch { order.value = null; }
      finally { loading.value = false; }
    }

    async function cancelOrder() {
      if (!confirm('Вы уверены, что хотите отменить заказ?')) return;
      try {
        await axios.patch(`${API_URL}/orders/${route.params.id}/cancel`, {}, { headers: { Authorization: `Bearer ${authStore.token}` } });
        await loadOrder();
      } catch (err) { alert(err.response?.data?.error || 'Ошибка при отмене заказа'); }
    }

    onMounted(async () => {
      await loadOrder();
      const orderId = route.params.id;
      joinOrderRoom(orderId);
      onOrderStatusUpdate(handleStatusUpdate);
    });

    onUnmounted(() => {
      const orderId = route.params.id;
      leaveOrderRoom(orderId);
      offOrderStatusUpdate(handleStatusUpdate);
      if (liveToastTimer.value) clearTimeout(liveToastTimer.value);
    });

    return { order, loading, liveConnected, liveUpdate, progressSteps, getStatusLabel, getStatusClass, getPaymentLabel, getPaymentClass, getStepClass, getProgressWidth, formatDate, cancelOrder };
  }
};
</script>

<style scoped>
* { box-sizing: border-box; }
.od-wrap { max-width: 900px; margin: 0 auto; }

.od-loading { display: flex; align-items: center; gap: 12px; color: #888; padding: 3rem; justify-content: center; font-size: 15px; }
.od-spinner { width: 24px; height: 24px; border: 2px solid #e0dff8; border-top-color: #534ab7; border-radius: 50%; animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

.od-not-found { text-align: center; background: #fff; border: 1px solid #e0dff8; border-radius: 20px; padding: 4rem 2rem; }
.od-nf-icon { font-size: 52px; margin-bottom: 1rem; }
.od-not-found h2 { font-size: 22px; font-weight: 800; color: #26215c; margin-bottom: 0.5rem; }
.od-not-found p { font-size: 14px; color: #888; margin-bottom: 1.5rem; }
.od-btn-back-link { color: #534ab7; font-weight: 700; text-decoration: none; font-size: 14px; }

.od-breadcrumb { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #888; margin-bottom: 1.5rem; }
.od-breadcrumb-link { color: #534ab7; text-decoration: none; font-weight: 600; }
.od-breadcrumb-link:hover { text-decoration: underline; }

.od-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 1.5rem; gap: 1rem; }
.od-title { font-size: 26px; font-weight: 800; color: #26215c; }
.od-created { font-size: 13px; color: #888; margin-top: 4px; }
.od-status-badge { font-size: 14px; font-weight: 700; padding: 8px 18px; border-radius: 20px; white-space: nowrap; }
.badge-yellow { background: #fef9e7; color: #e59f0a; }
.badge-blue { background: #eef3ff; color: #3b6ef7; }
.badge-purple { background: #eeedfe; color: #534ab7; }
.badge-green { background: #e1f5ee; color: #1d9e75; }
.badge-red { background: #ffeaea; color: #e05252; }

/* PROGRESS CARD */
.od-progress-card { background: #fff; border: 1px solid #e0dff8; border-radius: 18px; padding: 1.5rem; margin-bottom: 1.5rem; }
.od-pc-title { font-size: 13px; font-weight: 700; color: #888; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 1.5rem; }
.od-steps-row { display: flex; justify-content: space-between; margin-bottom: 1rem; position: relative; }
.od-steps-row::before { content: ''; position: absolute; top: 18px; left: 10%; right: 10%; height: 2px; background: #e0dff8; z-index: 0; }
.od-step { display: flex; flex-direction: column; align-items: center; gap: 6px; flex: 1; position: relative; z-index: 1; }
.od-step-circle { width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 700; background: #f4f3ff; border: 2px solid #e0dff8; color: #bbb; transition: all 0.3s; }
.od-step-done .od-step-circle { background: #534ab7; border-color: #534ab7; color: #fff; }
.od-step-active .od-step-circle { background: #fff; border-color: #1d9e75; color: #1d9e75; box-shadow: 0 0 0 4px rgba(29,158,117,0.15); animation: pulse 2s infinite; }
@keyframes pulse { 0%,100% { box-shadow: 0 0 0 4px rgba(29,158,117,0.15); } 50% { box-shadow: 0 0 0 6px rgba(29,158,117,0.08); } }
.od-step-label { font-size: 12px; font-weight: 700; color: #bbb; text-align: center; }
.od-step-done .od-step-label { color: #534ab7; }
.od-step-active .od-step-label { color: #1d9e75; }
.od-step-sub { font-size: 10px; color: #ddd; text-align: center; }
.od-step-done .od-step-sub { color: #b0aee0; }
.od-step-active .od-step-sub { color: #88d4b8; }
.od-progress-bar { height: 5px; background: #e0dff8; border-radius: 5px; overflow: hidden; }
.od-progress-fill { height: 100%; background: linear-gradient(90deg, #534ab7, #1d9e75); border-radius: 5px; transition: width 0.6s ease; }

/* GRID */
.od-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; margin-bottom: 1.5rem; }
.od-card { background: #fff; border: 1px solid #e0dff8; border-radius: 16px; padding: 1.25rem; }
.od-card-title { font-size: 13px; font-weight: 700; color: #534ab7; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 1rem; }
.od-row { display: flex; justify-content: space-between; align-items: flex-start; font-size: 13px; padding: 6px 0; border-bottom: 1px solid #f4f3ff; gap: 8px; }
.od-row:last-child { border-bottom: none; }
.od-row span { color: #888; flex-shrink: 0; }
.od-row strong { color: #26215c; text-align: right; }
.od-row-green strong { color: #1d9e75; }
.od-service-row { display: flex; justify-content: space-between; align-items: flex-start; padding: 8px 0; border-bottom: 1px solid #f4f3ff; gap: 8px; }
.od-service-row:last-of-type { border-bottom: none; }
.od-svc-name { font-size: 14px; font-weight: 700; color: #26215c; }
.od-svc-desc { font-size: 12px; color: #888; }
.od-svc-price { font-size: 16px; font-weight: 800; color: #1d9e75; flex-shrink: 0; }
.od-total-row { display: flex; justify-content: space-between; align-items: center; padding-top: 10px; margin-top: 4px; border-top: 1.5px solid #e0dff8; font-size: 14px; font-weight: 700; color: #888; }
.od-total-val { font-size: 22px; font-weight: 800; color: #26215c; }
.od-pay-badge { font-size: 12px; font-weight: 700; padding: 4px 10px; border-radius: 20px; }
.pay-red { background: #ffeaea; color: #e05252; }
.pay-green { background: #e1f5ee; color: #1d9e75; }
.pay-yellow { background: #fef9e7; color: #e59f0a; }
.od-technician { display: flex; align-items: center; gap: 12px; }
.od-tech-avatar { width: 44px; height: 44px; border-radius: 50%; background: linear-gradient(135deg, #534ab7, #7f77dd); display: flex; align-items: center; justify-content: center; font-size: 18px; color: #fff; font-weight: 700; flex-shrink: 0; }
.od-tech-name { font-size: 15px; font-weight: 700; color: #26215c; }
.od-tech-phone { font-size: 13px; color: #888; }
.od-notes { grid-column: 1 / -1; }
.od-notes-text { font-size: 14px; color: #444; line-height: 1.7; background: #f4f3ff; border-radius: 10px; padding: 12px 16px; }

/* LIVE BADGE */
.od-live-badge { display: inline-flex; align-items: center; gap: 7px; background: #e1f5ee; color: #1d9e75; font-size: 12px; font-weight: 700; padding: 5px 12px; border-radius: 20px; margin-bottom: 12px; border: 1px solid #a7e0c8; }
.od-live-dot { width: 8px; height: 8px; border-radius: 50%; background: #1d9e75; animation: livePulse 1.5s infinite; }
@keyframes livePulse { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.5; transform: scale(0.75); } }
.od-live-toast { background: #534ab7; color: #fff; font-size: 14px; padding: 12px 18px; border-radius: 12px; margin-bottom: 1rem; display: flex; align-items: center; gap: 8px; box-shadow: 0 4px 20px rgba(83,74,183,0.3); animation: slideIn 0.3s ease; }
@keyframes slideIn { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }

/* ACTIONS */
.od-actions { display: flex; gap: 10px; justify-content: flex-end; }
.od-btn-secondary { background: #f4f3ff; color: #534ab7; padding: 11px 22px; border-radius: 10px; font-size: 14px; font-weight: 700; text-decoration: none; transition: background 0.15s; display: inline-block; }
.od-btn-secondary:hover { background: #eeedfe; }
.od-btn-cancel { background: #ffeaea; color: #e05252; border: none; padding: 11px 22px; border-radius: 10px; font-size: 14px; font-weight: 700; cursor: pointer; transition: background 0.15s; }
.od-btn-cancel:hover { background: #f5c6c6; }

@media (max-width: 650px) {
  .od-grid { grid-template-columns: 1fr; }
  .od-steps-row::before { display: none; }
  .od-step-sub { display: none; }
  .od-actions { flex-direction: column; }
}
</style>
